import { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  useTheme,
  alpha,
  Button,
  Chip
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import PowerIcon from '@mui/icons-material/Power';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// --- Mock Data Embedded ---
// (Extracted from the original BuildingScorecard component's scope)
const buildingEfficiencyTips = [
  {
    category: 'Lighting',
    icon: <LightbulbOutlinedIcon />,
    color: '#FFC107', // amber
    tips: [
      {
        title: 'LED Conversion',
        description: 'Replace fluorescent tubes and incandescent bulbs with LED alternatives to save up to 75% on lighting energy costs.',
        potential: 'High',
        roi: '1-2 years'
      },
      {
        title: 'Occupancy Sensors',
        description: 'Install occupancy sensors in bathrooms, conference rooms, and other areas with variable occupancy.',
        potential: 'Medium',
        roi: '1-3 years'
      },
      {
        title: 'Daylight Harvesting',
        description: 'Install light sensors that dim artificial lighting when sufficient daylight is available.',
        potential: 'Medium',
        roi: '2-4 years'
      }
    ]
  },
  {
    category: 'HVAC',
    icon: <AcUnitIcon />,
    color: '#2196F3', // blue
    tips: [
      {
        title: 'Programmable Thermostats',
        description: 'Implement programmable thermostats with setbacks during unoccupied hours. Consider a 7-10°F setback when the building is unoccupied.',
        potential: 'High',
        roi: '1-2 years'
      },
      {
        title: 'Regular Maintenance',
        description: 'Schedule regular maintenance for HVAC systems, including filter changes, coil cleaning, and refrigerant level checks.',
        potential: 'Medium',
        roi: '1 year'
      },
      {
        title: 'Economizer Optimization',
        description: 'Ensure economizers are working properly to use outside air for cooling when outdoor conditions permit.',
        potential: 'Medium',
        roi: '1-2 years'
      }
    ]
  },
  {
    category: 'Equipment & Plug Loads',
    icon: <PowerIcon />,
    color: '#9C27B0', // purple
    tips: [
      {
        title: 'Smart Power Strips',
        description: 'Use advanced power strips to eliminate phantom loads from equipment in standby mode.',
        potential: 'Low-Medium',
        roi: '1-2 years'
      },
      {
        title: 'Equipment Scheduling',
        description: 'Implement automatic computer shutdown policies and ensure equipment is turned off during non-business hours.',
        potential: 'Medium',
        roi: '<1 year'
      },
      {
        title: 'ENERGY STAR Equipment',
        description: 'When replacing office equipment, choose ENERGY STAR certified models that use 30-75% less electricity.',
        potential: 'Medium',
        roi: '3-5 years'
      }
    ]
  },
  {
    category: 'Building Envelope',
    icon: <BuildOutlinedIcon />,
    color: '#795548', // brown
    tips: [
      {
        title: 'Air Sealing',
        description: 'Identify and seal air leaks around windows, doors, and other penetrations in the building envelope.',
        potential: 'Medium',
        roi: '1-3 years'
      },
      {
        title: 'Window Treatments',
        description: 'Install window films or treatments to reduce solar heat gain during summer months.',
        potential: 'Medium',
        roi: '3-5 years'
      },
      {
        title: 'Roof Insulation',
        description: 'Improve roof insulation to reduce heat transfer and HVAC loads.',
        potential: 'High',
        roi: '3-7 years'
      }
    ]
  }
];
// --- End Embedded Mock Data ---

const ImprovementTips = () => {
  const theme = useTheme();
  const [selectedTipCategory, setSelectedTipCategory] = useState('Lighting');

  const handleTipCategoryChange = (category: string) => {
    setSelectedTipCategory(category);
  };

  const selectedTips = buildingEfficiencyTips.find(cat => cat.category === selectedTipCategory)?.tips || [];
  const selectedTipColor = buildingEfficiencyTips.find(cat => cat.category === selectedTipCategory)?.color || theme.palette.primary.main;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Building Energy Efficiency Improvement Tips
        </Typography>

        <Grid container spacing={3}>
          {/* Category Selector */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Improvement Categories
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {buildingEfficiencyTips.map((category) => (
                <Button
                  key={category.category}
                  variant={selectedTipCategory === category.category ? 'contained' : 'outlined'}
                  color="primary" // Keep primary, background color handles visual selection
                  onClick={() => handleTipCategoryChange(category.category)}
                  startIcon={category.icon}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    py: 1.5,
                    bgcolor: selectedTipCategory === category.category ? alpha(category.color, 0.9) : 'transparent',
                    borderColor: alpha(category.color, 0.5),
                    color: selectedTipCategory === category.category ? '#FFF' : category.color,
                    '&:hover': {
                      bgcolor: selectedTipCategory === category.category ? alpha(category.color, 0.8) : alpha(category.color, 0.05),
                      borderColor: alpha(category.color, 0.7), // Ensure hover border is visible
                      color: selectedTipCategory === category.category ? '#FFF' : category.color, // Maintain text color on hover
                    }
                  }}
                >
                  {category.category}
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px dashed ${alpha(theme.palette.info.main, 0.3)}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon sx={{ mr: 1, fontSize: '1rem', color: theme.palette.info.main }} />
                Implementation Timeline
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Focus on high potential, quick ROI measures first, then progressively implement other recommendations as budget allows.
              </Typography>
            </Box>
          </Grid>

          {/* Tips for Selected Category */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                {selectedTipCategory} Recommendations
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {selectedTips.map((tip, index) => (
                <Grid item xs={12} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: alpha(selectedTipColor, 0.05),
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(selectedTipColor, 0.1)}`
                    }}
                  >
                    <Box
                      sx={{
                        py: 0.5,
                        px: 2,
                        bgcolor: alpha(selectedTipColor, 0.1),
                        borderBottom: `1px solid ${alpha(selectedTipColor, 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: selectedTipColor }}>
                        {tip.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`Potential: ${tip.potential}`}
                          size="small"
                          sx={{
                            bgcolor: alpha(selectedTipColor, 0.1),
                            color: selectedTipColor,
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                        <Chip
                          label={`ROI: ${tip.roi}`}
                          size="small"
                          sx={{
                            bgcolor: alpha(selectedTipColor, 0.1),
                            color: selectedTipColor,
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2">
                        {tip.description}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="primary"
                endIcon={<ArrowForwardIcon />}
                // Note: This button likely needs its onClick handler defined
                // if it should navigate or perform an action.
                // For now, it's just a visual element.
              >
                Get Detailed Efficiency Report
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ImprovementTips; 